import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { validationMessages } from '@sparklayer-io/spark-dash-core/types/errors';
import { Alert } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardHandlers } from '../../../lib/dashboard-page';
import { trimProtocol } from '../lib/trim-protocol';
import { AuthButton } from './auth-button';
import { FloatingInput } from './floating-input';
import { TermsCheckbox } from './terms-checkbox';

const COMPONENT = 'features:auth.components.enterprise_register_form' satisfies I18nPrefix;
const COMMON = 'features:auth.components.common' satisfies I18nPrefix;

type EnterpriseRegisterFormValues = {
  email: string;
  name: string;
  companyName: string;
  companyWebsite: string;
  password: string;
};

type EnterpriseRegisterFormProps = {
  handlers: DashboardHandlers;
};

export function EnterpriseRegisterForm({ handlers }: EnterpriseRegisterFormProps) {
  const { apiHandler, authHandler, navigationHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [bannerMessages, setBannerMessages] = useState<string[]>([]);

  const form = useAppForm({
    defaultValues: {
      email: '',
      name: '',
      companyName: '',
      companyWebsite: '',
      password: '',
    } satisfies EnterpriseRegisterFormValues,
    onSubmit: async ({ value }) => {
      setBannerMessages([]);
      setLoading(true);
      try {
        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });

        await apiHandler.fetch({
          method: 'POST',
          path: '/register-enterprise',
          payload: {
            email: value.email,
            name: value.name,
            company_name: value.companyName,
            company_website: `https://${value.companyWebsite}`,
            app_registration_source: 'enterprise_sales_pipeline_tracked_link',
            password: value.password,
          },
        });

        await authHandler.reloadUser();

        navigationHandler.navigate('/integrations/platform', {
          excludeSiteId: true,
          excludeSiteEnv: true,
        });
      } catch (err) {
        setLoading(false);
        const messages = validationMessages(err);
        if (messages) {
          setBannerMessages(messages);
        } else {
          setBannerMessages([t('errors:generic')]);
          throw err;
        }
        return;
      }
      setLoading(false);
    },
  });

  return (
    <>
      {bannerMessages.length > 0 && (
        <Alert variant="warning" headingLevel="h3">
          {bannerMessages.map((message) => (
            <p key={message} className="text-sm">
              {message}
            </p>
          ))}
        </Alert>
      )}
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.Field name="email">
          {(field) => (
            <FloatingInput
              name="email"
              type="email"
              label={t(`${COMPONENT}.email_label`)}
              autoComplete="email"
              required
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>

        <form.Field name="name">
          {(field) => (
            <FloatingInput
              name="name"
              label={t(`${COMPONENT}.name_label`)}
              autoComplete="name"
              required
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>

        <form.Field name="companyName">
          {(field) => (
            <FloatingInput
              name="company-name"
              label={t(`${COMPONENT}.company_name_label`)}
              required
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>

        <form.Field name="companyWebsite">
          {(field) => (
            <FloatingInput
              name="company-website"
              label={t(`${COMPONENT}.company_website_label`)}
              prefix="https://"
              required
              value={field.state.value}
              onChange={(value) => field.handleChange(trimProtocol(value))}
            />
          )}
        </form.Field>

        <form.Field name="password">
          {(field) => (
            <FloatingInput
              name="password"
              type="password"
              label={t(`${COMPONENT}.password_label`)}
              minLength={12}
              maxLength={128}
              required
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>

        <TermsCheckbox
          agreePrefix={t(`${COMMON}.terms.agree_prefix`)}
          linkText={t(`${COMMON}.terms.terms_of_service`)}
          linkHref="https://www.sparklayer.io/terms-of-service"
        />

        <div>
          <AuthButton loading={loading}>{t(`${COMPONENT}.submit`)}</AuthButton>
        </div>
      </form>
    </>
  );
}
